import { Box, Grid, Stack, Typography } from "@mui/material";
import { ReactElement, useCallback, useMemo } from "react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import styled, { useTheme } from "styled-components";
import { useReport } from "../../../contexts/ReportContext";
import FertilizedBlack from "../../../assets/images/report_icons/process-icons-black/fertilized.png";
import FrozenBlack from "../../../assets/images/report_icons/process-icons-black/frozen.png";
import GoodForTransferBlack from "../../../assets/images/report_icons/process-icons-black/good-for-transfer.png";
import ImplantedBlack from "../../../assets/images/report_icons/process-icons-black/implanted.png";
import LiveBirthBlack from "../../../assets/images/report_icons/process-icons-black/live-birth.png";
import RetrievedBlack from "../../../assets/images/report_icons/process-icons-black/retrieved.png";
import ThawedBlack from "../../../assets/images/report_icons/process-icons-black/thawed.png";
import FertilizedBlue from "../../../assets/images/report_icons/process-icons-blue/fertilized.png";
import FrozenBlue from "../../../assets/images/report_icons/process-icons-blue/frozen.png";
import GoodForTransferBlue from "../../../assets/images/report_icons/process-icons-blue/good-for-transfer.png";
import ImplantedBlue from "../../../assets/images/report_icons/process-icons-blue/implanted.png";
import LiveBirthBlue from "../../../assets/images/report_icons/process-icons-blue/live-birth.png";
import RetrievedBlue from "../../../assets/images/report_icons/process-icons-blue/retrieved.png";
import ThawedBlue from "../../../assets/images/report_icons/process-icons-blue/thawed.png";
import FertilizedGreen from "../../../assets/images/report_icons/process-icons-green/fertilized.png";
import FrozenGreen from "../../../assets/images/report_icons/process-icons-green/frozen.png";
import GoodForTransferGreen from "../../../assets/images/report_icons/process-icons-green/good-for-transfer.png";
import ImplantedGreen from "../../../assets/images/report_icons/process-icons-green/implanted.png";
import LiveBirthGreen from "../../../assets/images/report_icons/process-icons-green/live-birth.png";
import RetrievedGreen from "../../../assets/images/report_icons/process-icons-green/retrieved.png";
import ThawedGreen from "../../../assets/images/report_icons/process-icons-green/thawed.png";
import FertilizedOrange from "../../../assets/images/report_icons/process-icons-orange/fertilized.png";
import FrozenOrange from "../../../assets/images/report_icons/process-icons-orange/frozen.png";
import GoodForTransferOrange from "../../../assets/images/report_icons/process-icons-orange/good-for-transfer.png";
import ImplantedOrange from "../../../assets/images/report_icons/process-icons-orange/implanted.png";
import LiveBirthOrange from "../../../assets/images/report_icons/process-icons-orange/live-birth.png";
import RetrievedOrange from "../../../assets/images/report_icons/process-icons-orange/retrieved.png";
import ThawedOrange from "../../../assets/images/report_icons/process-icons-orange/thawed.png";
import FertilizedPink from "../../../assets/images/report_icons/process-icons-pink/fertilized.png";
import FrozenPink from "../../../assets/images/report_icons/process-icons-pink/frozen.png";
import GoodForTransferPink from "../../../assets/images/report_icons/process-icons-pink/good-for-transfer.png";
import ImplantedPink from "../../../assets/images/report_icons/process-icons-pink/implanted.png";
import LiveBirthPink from "../../../assets/images/report_icons/process-icons-pink/live-birth.png";
import RetrievedPink from "../../../assets/images/report_icons/process-icons-pink/retrieved.png";
import ThawedPink from "../../../assets/images/report_icons/process-icons-pink/thawed.png";
import FertilizedPurple from "../../../assets/images/report_icons/process-icons-purple/fertilized.png";
import FrozenPurple from "../../../assets/images/report_icons/process-icons-purple/frozen.png";
import GoodForTransferPurple from "../../../assets/images/report_icons/process-icons-purple/good-for-transfer.png";
import ImplantedPurple from "../../../assets/images/report_icons/process-icons-purple/implanted.png";
import LiveBirthPurple from "../../../assets/images/report_icons/process-icons-purple/live-birth.png";
import RetrievedPurple from "../../../assets/images/report_icons/process-icons-purple/retrieved.png";
import ThawedPurple from "../../../assets/images/report_icons/process-icons-purple/thawed.png";
import FertilizedRed from "../../../assets/images/report_icons/process-icons-red/fertilized.png";
import FrozenRed from "../../../assets/images/report_icons/process-icons-red/frozen.png";
import GoodForTransferRed from "../../../assets/images/report_icons/process-icons-red/good-for-transfer.png";
import ImplantedRed from "../../../assets/images/report_icons/process-icons-red/implanted.png";
import LiveBirthRed from "../../../assets/images/report_icons/process-icons-red/live-birth.png";
import RetrievedRed from "../../../assets/images/report_icons/process-icons-red/retrieved.png";
import ThawedRed from "../../../assets/images/report_icons/process-icons-red/thawed.png";
import FertilizedYellow from "../../../assets/images/report_icons/process-icons-yellow/fertilized.png";
import FrozenYellow from "../../../assets/images/report_icons/process-icons-yellow/frozen.png";
import GoodForTransferYellow from "../../../assets/images/report_icons/process-icons-yellow/good-for-transfer.png";
import ImplantedYellow from "../../../assets/images/report_icons/process-icons-yellow/implanted.png";
import LiveBirthYellow from "../../../assets/images/report_icons/process-icons-yellow/live-birth.png";
import RetrievedYellow from "../../../assets/images/report_icons/process-icons-yellow/retrieved.png";
import ThawedYellow from "../../../assets/images/report_icons/process-icons-yellow/thawed.png";
import {
  Attrition,
  AttritionGraph,
  AttritionGraphItem,
} from "../../../pages/EggFreezeCalculator/types";
import { useApp } from "../../../contexts/AppContext";

interface ProcessStepProps {
  icon: ReactElement;
  status: string;
  values: AttritionGraphItem;
  stage: "egg" | "embryo" | "baby";
  value: number;
  subtitle?: string;
  commonDomain: [number, number];
}

const CardContainer = styled.div`
  border-radius: 4px;
  border: 1px solid #f5f3ff;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  height: 600px;
  width: 100%;
  padding: 0;
`;

const GraphContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 0;
  max-width: 100%;

  & .recharts-wrapper {
    left: -6px;
  }
`;

const icons: { [step: string]: { [color: string]: ReactElement } } = {
  Retrieved: {
    red: <img src={RetrievedRed} />,
    blue: <img src={RetrievedBlue} />,
    green: <img src={RetrievedGreen} />,
    yellow: <img src={RetrievedYellow} />,
    purple: <img src={RetrievedPurple} />,
    orange: <img src={RetrievedOrange} />,
    pink: <img src={RetrievedPink} />,
    black: <img src={RetrievedBlack} />,
  },
  Frozen: {
    red: <img src={FrozenRed} />,
    blue: <img src={FrozenBlue} />,
    green: <img src={FrozenGreen} />,
    yellow: <img src={FrozenYellow} />,
    purple: <img src={FrozenPurple} />,
    orange: <img src={FrozenOrange} />,
    pink: <img src={FrozenPink} />,
    black: <img src={FrozenBlack} />,
  },
  Thawed: {
    red: <img src={ThawedRed} />,
    blue: <img src={ThawedBlue} />,
    green: <img src={ThawedGreen} />,
    yellow: <img src={ThawedYellow} />,
    purple: <img src={ThawedPurple} />,
    orange: <img src={ThawedOrange} />,
    pink: <img src={ThawedPink} />,
    black: <img src={ThawedBlack} />,
  },
  Fertilized: {
    red: <img src={FertilizedRed} />,
    blue: <img src={FertilizedBlue} />,
    green: <img src={FertilizedGreen} />,
    yellow: <img src={FertilizedYellow} />,
    purple: <img src={FertilizedPurple} />,
    orange: <img src={FertilizedOrange} />,
    pink: <img src={FertilizedPink} />,
    black: <img src={FertilizedBlack} />,
  },
  "Good for transfer": {
    red: <img src={GoodForTransferRed} />,
    blue: <img src={GoodForTransferBlue} />,
    green: <img src={GoodForTransferGreen} />,
    yellow: <img src={GoodForTransferYellow} />,
    purple: <img src={GoodForTransferPurple} />,
    orange: <img src={GoodForTransferOrange} />,
    pink: <img src={GoodForTransferPink} />,
    black: <img src={GoodForTransferBlack} />,
  },
  Implanted: {
    red: <img src={ImplantedRed} />,
    blue: <img src={ImplantedBlue} />,
    green: <img src={ImplantedGreen} />,
    yellow: <img src={ImplantedYellow} />,
    purple: <img src={ImplantedPurple} />,
    orange: <img src={ImplantedOrange} />,
    pink: <img src={ImplantedPink} />,
    black: <img src={ImplantedBlack} />,
  },
  "Live birth": {
    red: <img src={LiveBirthRed} />,
    blue: <img src={LiveBirthBlue} />,
    green: <img src={LiveBirthGreen} />,
    yellow: <img src={LiveBirthYellow} />,
    purple: <img src={LiveBirthPurple} />,
    orange: <img src={LiveBirthOrange} />,
    pink: <img src={LiveBirthPink} />,
    black: <img src={LiveBirthBlack} />,
  },
};

const ProcessSteps: React.FC<{
  age: number;
  attrition: Attrition;
  attritionGraph: AttritionGraph;
}> = ({ attrition, attritionGraph, age }) => {
  const { colorStyle } = useReport();
  const iconByStep = useCallback(
    (step: string, color: string) => {
      if (!icons[step]) {
        throw new Error(`Invalid step: ${step}`);
      }
      if (!icons[step][color]) {
        throw new Error(`Invalid color: ${color}`);
      }
      return icons[step][color];
    },
    [colorStyle]
  );

  const { translate } = useApp();
  const translations = translate("eggfreeze");

  const statusesMeta = useMemo(() => {
    return [
      {
        status: translations.report.processSteps.status.retrieved,
        icon: iconByStep("Retrieved", colorStyle),
        stage: "egg",
        subtitle: "",
        attrition: attrition.retrieved,
        graphValues: attritionGraph.retrieved,
      },
      {
        status: translations.report.processSteps.status.frozen,
        icon: iconByStep("Frozen", colorStyle),
        stage: "egg",
        subtitle: `0 Non-mature eggs`,
        attrition: attrition.frozen,
        graphValues: attritionGraph.frozen,
      },
      {
        status: translations.report.processSteps.status.thawed,
        icon: iconByStep("Thawed", colorStyle),
        stage: "egg",
        subtitle: `0 Not survived thawing`,
        attrition: attrition.thawed,
        graphValues: attritionGraph.thawed,
      },
      {
        status: translations.report.processSteps.status.fertilized,
        icon: iconByStep("Fertilized", colorStyle),
        stage: "embryo",
        subtitle: `0 Failed fertilization`,
        attrition: attrition.fertilized,
        graphValues: attritionGraph.fertilized,
      },
      {
        status: translations.report.processSteps.status.goodForTransfer,
        icon: iconByStep("Good for transfer", colorStyle),
        stage: "embryo",
        subtitle: `0 Not good for transfer`,
        attrition: attrition.good_embryos,
        graphValues: attritionGraph.good_embryos,
      },
      {
        status: translations.report.processSteps.status.implanted,
        icon: iconByStep("Implanted", colorStyle),
        stage: "embryo",
        subtitle: `0 Failed implantations`,
        attrition: attrition.implanted,
        graphValues: attritionGraph.implanted,
      },
      {
        status: translations.report.processSteps.status.liveBirth,
        icon: iconByStep("Live birth", colorStyle),
        stage: "baby",
        subtitle: `0 Pregnancy loss`,
        attrition: attrition.livebirth,
        graphValues: attritionGraph.livebirth,
      },
    ];
  }, [attrition, attritionGraph, colorStyle]);

  console.info("statusesMeta", statusesMeta);

  const commonDomain = useMemo<[number, number]>(() => {
    const maxValue = Object.values(attritionGraph)
      .map((item) => Math.max(item.left, item.middle, item.right))
      .reduce((acc, curr) => Math.max(acc, curr), 0);
    return [0, maxValue * 1.7];
  }, [attrition, attritionGraph]);

  // const areaChartData = useMemo(() => {
  //   return statusesMeta.map((statusMeta) => ({
  //     x: statusMeta.status, // Label for the X-axis
  //     y: statusMeta.rawValue || 0, // Use rawValue for the Y-axis
  //   }));
  // }, [statusesMeta]);

  return (
    <Grid
      container
      spacing={0.5}
      flexWrap={"nowrap"}
      justifyContent="space-between"
    >
      {statusesMeta.map((statusMeta, index) => {
        return (
          <Grid item key={index} xs={4}>
            <ProcessStep
              commonDomain={commonDomain}
              icon={statusMeta.icon}
              status={statusMeta.status}
              stage={statusMeta.stage as any}
              value={Number(statusesMeta[index].attrition)}
              values={statusesMeta[index].graphValues}
              subtitle={statusMeta.subtitle}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

const ProcessStep: React.FC<ProcessStepProps> = ({
  icon,
  status,
  subtitle,
  values,
  stage,
  value,
  commonDomain,
  // chartData,
}) => {
  const { translate } = useApp();

  const eggsString = useMemo(() => {
    const numValue = Number(value);
    switch (stage) {
      case "egg":
        return numValue <= 1
          ? translate("general", "egg")
          : translate("general", "eggs");
      case "embryo":
        return numValue <= 1
          ? translate("general", "embryo")
          : translate("general", "embryos");
      default:
        return numValue <= 1
          ? translate("general", "baby")
          : translate("general", "babies");
    }
  }, [value, stage]);

  const { colorStyle } = useReport();
  const theme: any = useTheme();

  // console.info("chartData", chartData);

  const points = useMemo(() => {
    return [
      { x: 0, y: values.left },
      { x: 2, y: values.middle },
      { x: 3, y: values.right },
    ];
  }, [values]);

  return (
    <CardContainer>
      <Box height="20%">
        <Stack
          padding={2}
          width={"100%"}
          textAlign={"left"}
          direction="column"
          alignItems="flexstart"
          height={100}
        >
          <Box width={10} sx={{ "& img": { width: 25 } }}>
            {icon}
          </Box>
          <Typography variant="report.steps.title" fontSize={16}>
            {status}
          </Typography>
        </Stack>
        <Stack
          paddingTop={2}
          width={"100%"}
          justifyContent={"center"}
          direction={"row"}
          gap={1}
          alignItems={"flex-end"}
        >
          <Stack spacing={1} direction={"row"} alignItems={"center"}>
            <Typography
              fontSize={24}
              variant="report.steps.large"
              color="textPrimary"
            >
              {value}
            </Typography>
            <Typography fontSize={18} variant="report.card.info.normal">
              {eggsString}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <GraphContainer>
        <ResponsiveContainer width="110%" aspect={0} height={"100%"}>
          <AreaChart data={points}>
            <defs>
              <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={theme.palette.reportColorStyles[colorStyle].dark}
                  stopOpacity={1}
                />
                <stop
                  offset="100%"
                  stopColor={theme.palette.reportColorStyles[colorStyle].dark}
                  stopOpacity={0.5}
                />
              </linearGradient>
            </defs>
            <YAxis type="number" domain={commonDomain} hide />
            <Area
              type="monotone"
              dataKey="y"
              stroke={theme.palette.reportColorStyles[colorStyle].dark}
              fill="url(#colorGradient)"
              strokeWidth={2} 
            />
          </AreaChart>
        </ResponsiveContainer>
      </GraphContainer>
    </CardContainer>
  );
};

export default ProcessSteps;
